import type { ReactNode } from 'react'
import { timestampDate } from '@bufbuild/protobuf/wkt'
import { Pencil, Plus, UserPlus } from 'lucide-react'

import type { EventAddon } from '@/gen/cortado/v1/event_plan_pb'
import type { Booking, Event } from '@/gen/cortado/v1/inquiries_pb'
import type { JobEventStaff } from '@/gen/cortado/v1/jobs_pb'
import { UserSchedulingRoleEnum, UserSchedulingStatusEnum } from '@/gen/cortado/v1/settings_pb'
import { getAvatarColor, getInitials } from '@/lib/avatar-utils'
import { formatQuestionnaireAnswer, sortedQuestionnaireItems } from '@/lib/booking-questionnaire'
import {
  describeTimelineChanges,
  formatBlockDuration,
  formatBlockTime,
  isDriveKind,
  timelineKindColor,
} from '@/lib/event-timeline'
import { cn, formatFullName } from '@/lib/utils'
import { useEventTimeline } from '@/stores/event-timeline.store'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { ActionGate } from '@/components/shared/action-gate'
import { questionnaireDeliveryLabel } from '@/components/events/id/booking-questionnaire-card'

interface RunSheetSectionProps {
  /** The anchor the readiness chips and the job page link to (EVENT_SECTION_IDS). */
  id: string
  number: string
  title: string
  /** The muted line beside the title: counts, where the data came from. */
  summary?: ReactNode
  /** The header's one action, which opens a sheet; the section itself reads. */
  action?: ReactNode
  children: ReactNode
}

/** One numbered section of the run sheet: "01 Staffing", its summary, one action, and its body. */
export function RunSheetSection({ id, number, title, summary, action, children }: RunSheetSectionProps) {
  return (
    <section id={id} aria-labelledby={`${id}-title`} className="scroll-mt-4 py-5">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
        <div className="flex min-w-0 flex-wrap items-baseline gap-x-3 gap-y-1">
          <span aria-hidden="true" className="font-mono text-xs font-medium tracking-widest text-brand">
            {number}
          </span>
          <h2 id={`${id}-title`} className="text-base font-semibold">
            {title}
          </h2>
          {summary && <span className="text-xs text-muted-foreground">{summary}</span>}
        </div>
        {action}
      </div>
      {children}
    </section>
  )
}

const STAFF_STATUS: Partial<Record<UserSchedulingStatusEnum, { label: string; className: string }>> = {
  [UserSchedulingStatusEnum.USER_SCHEDULING_STATUS_PENDING]: {
    label: 'Pending',
    className: 'border-status-warning-border text-status-warning-fg',
  },
  [UserSchedulingStatusEnum.USER_SCHEDULING_STATUS_ACCEPTED]: {
    label: 'Confirmed',
    className: 'border-status-success-border text-status-success-fg',
  },
  [UserSchedulingStatusEnum.USER_SCHEDULING_STATUS_REJECTED]: {
    label: 'Declined',
    className: 'border-status-danger-border text-status-danger-fg',
  },
}

function StaffPill({ person }: { person: JobEventStaff }) {
  const name = formatFullName(person.firstName, person.lastName, 'Unnamed')
  const status = STAFF_STATUS[person.status]
  const isLead = person.role === UserSchedulingRoleEnum.USER_SCHEDULING_ROLE_LEAD
  return (
    <li
      aria-label={`${name} · ${isLead ? 'Lead' : 'Barista'}${status ? ` · ${status.label}` : ''}`}
      className="inline-flex items-center gap-2 rounded-full border py-1 pr-3 pl-1 text-sm"
    >
      <span
        aria-hidden="true"
        className={cn(
          'flex size-6 items-center justify-center rounded-full text-xs font-semibold text-white',
          getAvatarColor(name),
        )}
      >
        {getInitials(person.firstName, person.lastName)}
      </span>
      <span className="font-medium">{name}</span>
      <Badge variant={isLead ? 'info' : 'outline'}>{isLead ? 'Lead' : 'Barista'}</Badge>
      {status && <span className={cn('rounded-full border px-2 text-xs', status.className)}>{status.label}</span>}
    </li>
  )
}

function plural(n: number, noun: string): string {
  return `${n} ${noun}${n === 1 ? '' : 's'}`
}

interface StaffingSectionProps {
  id: string
  event: Event
  /** The event's staff from the job's event list; undefined while that list loads. */
  staff: JobEventStaff[] | undefined
  onAssign: () => void
}

/** 01 Staffing: who is on the event, Lead first, with each person's answer. Assigning happens in the sheet. */
export function StaffingSection({ id, event, staff, onAssign }: StaffingSectionProps) {
  const needed = [
    event.baristaCount != null && event.baristaCount > 0 ? `${event.baristaCount} staff` : '',
    event.cartCount != null && event.cartCount > 0 ? plural(event.cartCount, 'cart') : '',
  ]
    .filter(Boolean)
    .join(' · ')
  const hasStaff = !!staff && staff.length > 0

  return (
    <RunSheetSection
      id={id}
      number="01"
      title="Staffing"
      summary={needed ? `${needed} needed` : undefined}
      action={
        <ActionGate organizationId={event.organizationId} permission="EventsManage" verb="assign" noun="staff">
          <Button type="button" size="sm" variant={hasStaff ? 'ghost' : 'outline'} onClick={onAssign}>
            <UserPlus data-icon="inline-start" />
            {hasStaff ? 'Change staff' : 'Assign staff'}
          </Button>
        </ActionGate>
      }
    >
      {staff === undefined ? (
        <div aria-label="Loading staff" className="flex gap-2">
          <Skeleton className="h-8 w-40" />
          <Skeleton className="h-8 w-40" />
        </div>
      ) : hasStaff ? (
        <ul aria-label="Staff on this event" className="flex flex-wrap items-center gap-2">
          {staff.map((person) => (
            <StaffPill key={person.schedulingId || person.userId} person={person} />
          ))}
        </ul>
      ) : (
        <p className="text-sm text-muted-foreground">No one assigned yet.</p>
      )}
    </RunSheetSection>
  )
}

function addonQuantity(addon: EventAddon): string {
  if (addon.packQuantity > 1) return `× ${addon.packQuantity}`
  return ''
}

interface AddonsSectionProps {
  id: string
  addons: EventAddon[] | undefined
  isError: boolean
}

/** 02 Add-ons: what the client ticked on acceptance, with its prep note and how many to pack. */
export function AddonsSection({ id, addons, isError }: AddonsSectionProps) {
  return (
    <RunSheetSection id={id} number="02" title="Add-ons" summary="Ticked by the client on acceptance">
      {isError ? (
        <p className="text-sm text-muted-foreground">Couldn't load the add-ons.</p>
      ) : addons === undefined ? (
        <div aria-label="Loading add-ons" className="flex flex-col gap-2">
          <Skeleton className="h-5 w-2/3" />
          <Skeleton className="h-5 w-1/2" />
        </div>
      ) : addons.length === 0 ? (
        <p className="text-sm text-muted-foreground">No add-ons on this event.</p>
      ) : (
        <ul aria-label="Add-ons" className="divide-y">
          {addons.map((addon) => (
            <li key={addon.lineItemId || addon.addonOfferId} className="flex flex-wrap items-center gap-3 py-2 text-sm">
              <span aria-hidden="true" className="size-2 shrink-0 rounded-full bg-status-success-fg" />
              <span className="min-w-40 font-medium">{addon.name}</span>
              {addon.description && <span className="flex-1 text-muted-foreground">{addon.description}</span>}
              {addonQuantity(addon) && (
                <span className="ml-auto font-financial text-xs text-muted-foreground">{addonQuantity(addon)}</span>
              )}
            </li>
          ))}
        </ul>
      )}
    </RunSheetSection>
  )
}

interface TimelineSectionProps {
  id: string
  event: Event
  onEdit: () => void
}

/**
 * 03 Timeline: the saved run of show, read-only. Editing and Regenerate live
 * in the Timeline sheet, so the page never has an editor of its own. When the
 * event's details changed since the blocks were built, the notice says what.
 */
export function TimelineSection({ id, event, onEdit }: TimelineSectionProps) {
  const hasEventDate = !!event.eventDate
  const { timeline, isLoading, isError } = useEventTimeline(hasEventDate ? event.id : '')
  const timeZone = timeline?.timezone || event.eventTimezone || undefined
  const changes = timeline ? describeTimelineChanges(timeline.changes) : []

  let summary: string | undefined
  if (timeline) {
    summary = timeline.isCustomized ? 'Edited by hand' : 'Built from the event details'
  }

  let body: ReactNode
  if (!hasEventDate) {
    body = <p className="text-sm text-muted-foreground">Set the event date to build a timeline.</p>
  } else if (isLoading) {
    body = (
      <div aria-label="Loading timeline" className="flex flex-col gap-2">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-6 w-full" />
        ))}
      </div>
    )
  } else if (isError || !timeline) {
    body = <p className="text-sm text-muted-foreground">Couldn't load the timeline.</p>
  } else {
    body = (
      <div className="flex flex-col gap-3">
        {changes.length > 0 && (
          <p role="status" className="rounded-md border border-status-warning-border bg-status-warning-bg/60 px-3 py-2 text-xs text-status-warning-fg">
            Details changed since this timeline was built: {changes.join(', ')}. Open it to regenerate.
          </p>
        )}
        <ol aria-label="Timeline blocks">
          {timeline.blocks.map((block, index) => {
            const start = block.start ? timestampDate(block.start) : null
            const unknownDrive = isDriveKind(block.kind) && block.minutes === 0 && !timeline.driveTimeKnown
            return (
              <li key={block.id || index} className="flex items-center gap-3 py-1.5 text-sm">
                <span aria-hidden="true" className={cn('h-5 w-0.5 shrink-0 rounded-full', timelineKindColor(block.kind))} />
                <span className="w-20 shrink-0 font-financial text-xs">
                  {start ? formatBlockTime(start, timeZone) : ''}
                  {block.dayOffset !== 0 && (
                    <span className="ml-1 text-muted-foreground">{block.dayOffset > 0 ? `+${block.dayOffset}d` : `${block.dayOffset}d`}</span>
                  )}
                </span>
                <span className="min-w-0 flex-1">
                  {block.label}
                  {block.note && <span className="text-muted-foreground"> · {block.note}</span>}
                </span>
                <span className="text-xs text-muted-foreground">
                  {unknownDrive ? 'Unknown' : formatBlockDuration(block.minutes)}
                </span>
              </li>
            )
          })}
        </ol>
        {!timeline.driveTimeKnown && (
          <p className="text-xs text-muted-foreground">
            Drive time is unknown. Add the event location and your business address to estimate it.
          </p>
        )}
      </div>
    )
  }

  return (
    <RunSheetSection
      id={id}
      number="03"
      title="Timeline"
      summary={summary}
      action={
        hasEventDate && (
          <ActionGate organizationId={event.organizationId} permission="EventsManage" verb="edit" noun="timelines">
            <Button type="button" size="sm" variant="ghost" onClick={onEdit}>
              <Pencil data-icon="inline-start" />
              Edit timeline
            </Button>
          </ActionGate>
        )
      }
    >
      {body}
    </RunSheetSection>
  )
}

interface OnSiteSectionProps {
  id: string
  event: Event
  /** The event's booking; undefined while it loads. */
  booking: Booking | undefined
  onEdit: () => void
}

/**
 * 05 On-site details: the booking questionnaire's questions with the client's
 * answers, plus the on-site contact. Questions are edited in the questionnaire
 * sheet.
 */
export function OnSiteSection({ id, event, booking, onEdit }: OnSiteSectionProps) {
  const items = booking ? sortedQuestionnaireItems(booking.questionnaire) : []
  const delivery = booking ? questionnaireDeliveryLabel(booking.questionnaireSentAt, booking.questionnaireReturnedAt) : undefined
  const contact = event.contact
  const contactName = contact ? formatFullName(contact.firstName, contact.lastName, '') : ''
  const contactLine = [contactName, contact?.phoneNumber].filter(Boolean).join(' · ')

  return (
    <RunSheetSection
      id={id}
      number="05"
      title="On-site details"
      summary={booking ? ['From the client questionnaire', delivery].filter(Boolean).join(' · ') : undefined}
      action={
        booking && (
          <ActionGate organizationId={booking.organizationId} permission="EventsManage" verb="edit" noun="the questionnaire">
            <Button type="button" size="sm" variant={items.length > 0 ? 'ghost' : 'outline'} onClick={onEdit}>
              {items.length > 0 ? <Pencil data-icon="inline-start" /> : <Plus data-icon="inline-start" />}
              {items.length > 0 ? 'Edit questions' : 'Add questions'}
            </Button>
          </ActionGate>
        )
      }
    >
      {!booking ? (
        <div aria-label="Loading questionnaire" className="flex flex-col gap-2">
          <Skeleton className="h-5 w-1/2" />
          <Skeleton className="h-5 w-2/3" />
        </div>
      ) : (
        <dl className="grid gap-x-7 gap-y-3 sm:grid-cols-2">
          {items.length === 0 && (
            <p className="text-sm text-muted-foreground sm:col-span-2">No questions on this booking yet.</p>
          )}
          {items.map((item) => {
            const answer = formatQuestionnaireAnswer(item)
            return (
              <div key={item.id} className="flex flex-col gap-0.5">
                <dt className="text-xs text-muted-foreground">{item.prompt}</dt>
                <dd className={cn('text-sm', !answer && 'text-muted-foreground italic')}>{answer || 'No answer yet'}</dd>
              </div>
            )
          })}
          {contactLine && (
            <div className="flex flex-col gap-0.5">
              <dt className="text-xs text-muted-foreground">On-site contact</dt>
              <dd className="text-sm">{contactLine}</dd>
            </div>
          )}
        </dl>
      )}
    </RunSheetSection>
  )
}
